/**
 * IARequirementsBanner Component
 * Simple contextual tip showing the most important thing to know/do next for IA
 * 
 * Design matches PIRequirementsBanner (Design 10 - minimal underline)
 */

import { type IARequirements } from '@/lib/ia-calculator-utils';

interface IARequirementsBannerProps {
  requirements: IARequirements | null;
  completedCount: number;
  totalSelected: number;
}

function getContextualTip(
  requirements: IARequirements | null,
  completedCount: number,
  totalSelected: number
): string | null {
  if (!requirements) return null;

  const { activitiesRequired, maxPoints } = requirements;

  // Priority 1: No activities selected at all
  if (totalSelected === 0) {
    return `Add improvement activities to begin. You need ${activitiesRequired} ${activitiesRequired === 1 ? 'activity' : 'activities'} for full IA credit (${maxPoints} points).`;
  }

  // Priority 2: Activities selected but none completed
  if (completedCount === 0) {
    return 'Complete your selected activities by marking "Yes" and adding attestation details to earn points.';
  }

  // Priority 3: Some activities still in progress (selected but not completed)
  const inProgress = totalSelected - completedCount;
  if (inProgress > 0 && completedCount < activitiesRequired) {
    return `${inProgress} ${inProgress === 1 ? 'activity' : 'activities'} still need attestation. Complete them to earn full credit.`;
  }

  // Priority 4: Completed some but not enough for full credit
  if (completedCount < activitiesRequired) {
    const needed = activitiesRequired - completedCount;
    return `You've completed ${completedCount} ${completedCount === 1 ? 'activity' : 'activities'}. Add and complete ${needed} more to reach full IA credit.`;
  }

  // All requirements met - hide the banner
  return null;
}

export function IARequirementsBanner({
  requirements,
  completedCount,
  totalSelected,
}: IARequirementsBannerProps) {
  const tip = getContextualTip(requirements, completedCount, totalSelected);

  // Don't render if no tip needed (requirements met)
  if (!tip) return null;

  // Design 10: Minimal underline with "NEXT STEP" label (matches PI)
  return (
    <div className="border-b-2 border-primary pb-3 mb-1">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-primary uppercase tracking-wide">Next Step</span>
        </div>
      <p className="text-sm text-gray-700 mt-1">{tip}</p>
    </div>
  );
}



